import { Link } from 'react-router-dom';
import { Star, History, User, Building2 } from 'lucide-react';
import type { TodayJumpItem } from '../../types';

const targetFor = (item: TodayJumpItem): string =>
  item.kind === 'PERSON' ? `/people/${item.id}` : `/organizations/${item.id}`;

function Chip({ item, favorite }: { item: TodayJumpItem; favorite: boolean }) {
  const Lead = favorite ? Star : item.kind === 'PERSON' ? User : Building2;
  return (
    <Link
      to={targetFor(item)}
      title={item.subtitle ? `${item.name} · ${item.subtitle}` : item.name}
      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:border-blast-400 dark:hover:border-blast-500 transition-colors"
    >
      <Lead className={`h-3.5 w-3.5 flex-shrink-0 ${favorite ? 'text-blast-500 dark:text-blast-400' : 'text-gray-400 dark:text-gray-500'}`} />
      <span className="max-w-[10rem] truncate">{item.name}</span>
    </Link>
  );
}

/**
 * "Jump back in" — a horizontally scrollable strip of favorite (★) and
 * recently-viewed (↻) people/orgs for one-tap return. Renders nothing when both
 * are empty (the agenda/attention sections carry the empty-state weight).
 */
export function JumpBackIn({
  favorites,
  recent,
}: {
  favorites: TodayJumpItem[];
  recent: TodayJumpItem[];
}) {
  // De-dupe: a favorite already shown shouldn't repeat as a recent chip.
  const favKeys = new Set(favorites.map((f) => `${f.kind}:${f.id}`));
  const recentFiltered = recent.filter((r) => !favKeys.has(`${r.kind}:${r.id}`));

  if (favorites.length === 0 && recentFiltered.length === 0) return null;

  return (
    <div>
      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
        <History className="h-3.5 w-3.5" /> Jump back in
      </h3>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {favorites.map((f) => (
          <Chip key={`fav-${f.kind}-${f.id}`} item={f} favorite />
        ))}
        {recentFiltered.map((r) => (
          <Chip key={`recent-${r.kind}-${r.id}`} item={r} favorite={false} />
        ))}
      </div>
    </div>
  );
}
